import bannerImage from '@assets/images/banner.jpg';
import { DebrisBadge } from '@features/ui-atoms/components/DebrisBadge';
import { VerifiedMemberBadge } from '@features/ui-atoms/components/VerifiedMemberBadge';
import Icon, { IconName } from '@features/ui-atoms/components/Icon';
import { styled } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import Typography from '@mui/joy/Typography';
import Image from 'next/image';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

export const HeaderLeft = styled('div')`
  display: flex;
  gap: 16px;
  align-items: center;
`;

export const HeaderRight = styled('div')`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const HeaderTitle = ({
  title,
  isDebris,
  memberSince,
}: {
  title?: string;
  isDebris?: boolean;
  memberSince?: Date;
}) => {
  const theme = useTheme();
  return (
    <Typography
      level="navbar-title"
      sx={{ color: theme.palette.white, display: 'flex', alignItems: 'center', gap: '8px' }}
    >
      {title}
      {memberSince && <VerifiedMemberBadge variant="white" memberSince={memberSince} />}
      {isDebris && <DebrisBadge />}
    </Typography>
  );
};

const BaseHeaderBackButton = ({ children, ...props }: ComponentPropsWithoutRef<'button'>) => (
  <button type="button" {...props}>
    <Icon width={14} name={IconName.arrowLeft} />
    {children}
  </button>
);

export const HeaderBackButton = styled(BaseHeaderBackButton)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
  backgroundColor: 'transparent !important',
  border: 'none',
  color: 'white',
  fontSize: '14px',
  height: '16px',
  fontWeight: 500,
  padding: 0,
  cursor: 'pointer',
  '& svg': {
    color: theme.vars.palette.white,
    fontSize: '14px',
    fontWeight: 500,
  },
  '&:hover': {
    color: theme.vars.palette.grey2,
  },
}));

interface HeaderProps {
  children: ReactNode;
}

const Header = ({ children }: HeaderProps) => (
  <HeaderContainer>
    <ImageWrapper>
      <Image
        src={bannerImage}
        alt="Header background"
        fill
        style={{ objectFit: 'cover' }}
        priority
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
      />
    </ImageWrapper>
    <HeaderContent>{children}</HeaderContent>
  </HeaderContainer>
);

export default Header;

const HeaderContainer = styled('header')`
  position: fixed;
  top: 0;
  left: var(--width-sidebar);
  right: 0;
  height: var(--height-header);
  z-index: var(--z-index-header);
`;

const ImageWrapper = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
`;

const HeaderContent = styled('div')`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 32px;
`;
